import { CheckCircle2, CircleAlert, Clock3, FileText, Headphones, Image as ImageIcon } from 'lucide-react';
import {
  type ArtifactStatus,
  type DailyBrief,
  type EvidenceBoundary,
  type LongevityGoalCard,
} from '../../features/execute/longevity';
import styles from './LongevityPages.module.css';

const artifactLabels: Array<{
  key: keyof DailyBrief['artifactStatuses'];
  label: string;
  Icon: typeof FileText;
}> = [
  { key: 'dailyBrief', label: '当前说明', Icon: FileText },
  { key: 'article', label: '真知解释', Icon: FileText },
  { key: 'practiceTask', label: '当前行动', Icon: CheckCircle2 },
  { key: 'podcastAudio', label: '可听讲解', Icon: Headphones },
  { key: 'visualCardSet', label: '复习卡片', Icon: ImageIcon },
];

export function getArtifactStatusText(status: ArtifactStatus) {
  switch (status) {
    case 'completed':
      return '已就绪';
    case 'degraded':
      return '待完善';
    case 'failed':
      return '待补齐';
    case 'processing':
      return '准备中';
    case 'pending':
    default:
      return '待准备';
  }
}

export function isArtifactReady(status: ArtifactStatus) {
  return status === 'completed' || status === 'degraded';
}

export function buildDailyVisualCards(brief: DailyBrief) {
  return [
    {
      title: '当前行动卡',
      text: brief.dailyActionLabel,
      eyebrow: '怎么做',
    },
    {
      title: '目标证据卡',
      text: brief.oneThesis,
      eyebrow: '为什么做',
    },
    {
      title: '安全边界卡',
      text: brief.safetyStop,
      eyebrow: '何时停',
    },
  ];
}

function statusDotClass(status: ArtifactStatus) {
  if (status === 'failed') return `${styles.statusDot} ${styles.statusDotFailed}`;
  if (status === 'degraded') return `${styles.statusDot} ${styles.statusDotDegraded}`;
  if (status === 'pending' || status === 'processing') return `${styles.statusDot} ${styles.statusDotPending}`;
  return styles.statusDot;
}

export function ArtifactStatusStrip({ statuses }: { statuses: DailyBrief['artifactStatuses'] }) {
  return (
    <div className={styles.statusStrip} aria-label="今日材料状态">
      {artifactLabels.map(({ key, label, Icon }) => {
        const status = statuses[key];
        return (
          <div className={styles.statusItem} key={key}>
            <div className={statusDotClass(status)} />
            <span className={styles.statusLabel}>
              <Icon size={14} strokeWidth={1.5} aria-hidden />
              {label} · {getArtifactStatusText(status)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const boundaryDescriptions: Record<string, string> = {
  你的资料: '来自你已经填写或上传的健康信息',
  通用知识: '适用于大多数人的健康原则',
  缺失数据: '由于信息不足，这部分建议的确定性较低',
  需要医生确认: '需要专业医生评估，不能自行判断',
};

export function formatEvidenceText(items: string[], fallback = '暂无。') {
  const text = items
    .map((item) => item.trim().replace(/[。；;]+$/u, ''))
    .filter(Boolean)
    .join('；');
  return text ? `${text}。` : fallback;
}

export function EvidenceBoundaryGrid({ boundary }: { boundary: EvidenceBoundary }) {
  const items: Array<{ label: string; text: string; strong?: boolean }> = [
    { label: '你的资料', text: formatEvidenceText(boundary.fromUserData, '暂无更多个人资料。') },
    { label: '通用知识', text: formatEvidenceText(boundary.generalGuidance, '只使用通用健康原则。') },
    { label: '缺失数据', text: formatEvidenceText(boundary.missingData) },
    { label: '需要医生确认', text: boundary.doctorBoundary, strong: true },
  ];

  return (
    <div className={styles.boundaryGrid}>
      {items.map((item) => (
        <div
          className={`${styles.boundaryCard} ${item.strong ? styles.boundaryCardStrong : ''}`}
          key={item.label}
        >
          <p className={styles.eyebrow}>{item.label}</p>
          <p className={styles.cardText}>{item.text}</p>
          <p className={styles.boundaryDescription}>{boundaryDescriptions[item.label]}</p>
        </div>
      ))}
    </div>
  );
}

export function GoalMiniGrid({ goals }: { goals: LongevityGoalCard[] }) {
  return (
    <div className={styles.goalPreviewGrid}>
      {goals.map((goal, index) => (
        <article className={styles.goalMiniCard} key={goal.id}>
          <span className={styles.goalMiniIndex}>{index + 1}</span>
          <div className={styles.goalMiniBody}>
            <h3 className={styles.goalMiniTitle}>{goal.title}</h3>
            <div className={styles.goalMiniAbilityRow}>
              {goal.abilityTags.slice(0, 2).map((tag) => (
                <span className={styles.goalMiniAbility} key={tag}>{tag}</span>
              ))}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

export function SafetyNotice({ children }: { children: string }) {
  return (
    <div className={styles.notice} role="note">
      <CircleAlert className={styles.noticeIcon} size={20} strokeWidth={1.5} aria-hidden />
      <p className={styles.noticeText}>{children}</p>
    </div>
  );
}

export function TimeBadge({ label }: { label: string }) {
  return (
    <span className={styles.taskMeta}>
      <Clock3 size={14} strokeWidth={1.5} aria-hidden />
      {label}
    </span>
  );
}
